import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, MapPin, Send, Sparkles } from "lucide-react";
import CitizenAttachment from "@/components/CitizenAttachment";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { REPORT_FORM } from "@/constants/testIds";
import { SuccessModal } from "@/pages/ComplaintForm";

const ReportForm = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    phone: "",
    description: "",
    location: "",
    when: "",
    media_url: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const err = {};
    if (form.name.trim().length < 2) err.name = "Informe seu nome.";
    if (form.phone.trim().length < 8) err.phone = "Telefone inválido.";
    if (form.description.trim().length < 5) err.description = "Descreva o fato (mínimo 5 caracteres).";
    if (form.location.trim().length < 2) err.location = "Informe o local do ocorrido.";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const useGPS = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocalização não disponível.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        update("location", `${pos.coords.latitude.toFixed(5)}, ${pos.coords.longitude.toFixed(5)}`);
        toast.success("Localização capturada.");
      },
      () => toast.error("Não foi possível obter a localização.")
    );
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await api.post("/contact/report", { ...form, media_url: form.media_url || null, when: form.when || null });
      setSuccess(true);
    } catch (err) {
      toast.error("Erro ao enviar. Tente novamente.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="max-w-2xl mx-auto px-4 md:px-6 pt-6 md:pt-10">
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1 text-sm font-bold uppercase tracking-wider text-[var(--fi-blue)] mb-4 hover:text-[var(--fi-red)] transition-colors"
      >
        <ChevronLeft className="w-4 h-4" />
        Voltar
      </button>

      <div className="mb-2 text-xs uppercase tracking-[0.28em] text-[var(--fi-red)] font-bold">
        Formulário
      </div>
      <h1 className="font-display text-4xl sm:text-5xl font-black uppercase text-[var(--fi-blue)] leading-[0.9] mb-2">
        Chamar Reportagem
      </h1>
      <div className="inline-flex items-center gap-2 rounded-full bg-[var(--fi-blue)]/5 text-[var(--fi-blue)] px-3 py-1 mt-1">
        <Sparkles className="w-4 h-4 text-[var(--fi-red)]" />
        <span className="text-xs uppercase tracking-widest font-bold">Seja nossa fonte!</span>
      </div>
      <p className="text-gray-600 mt-3 mb-6 max-w-lg">
        Compartilhe uma pauta ou peça a presença da nossa equipe no local do fato.
      </p>

      <form onSubmit={onSubmit} className="space-y-4 bg-white rounded-2xl p-5 sm:p-6 shadow-[0_8px_30px_rgba(13,27,75,0.08)]">
        <Field label="Nome completo *" error={errors.name}>
          <input
            data-testid={REPORT_FORM.name}
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            className="input"
            placeholder="Seu nome"
          />
        </Field>
        <Field label="Telefone / WhatsApp *" error={errors.phone}>
          <input
            data-testid={REPORT_FORM.phone}
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
            className="input"
            placeholder="(19) 9 9999-9999"
          />
        </Field>
        <Field label="Descrição do fato *" error={errors.description}>
          <textarea
            data-testid={REPORT_FORM.description}
            value={form.description}
            onChange={(e) => update("description", e.target.value)}
            rows={5}
            className="input"
            placeholder="Conte o que aconteceu..."
          />
        </Field>
        <Field label="Local do ocorrido *" error={errors.location}>
          <div className="flex gap-2">
            <input
              data-testid={REPORT_FORM.location}
              value={form.location}
              onChange={(e) => update("location", e.target.value)}
              className="input flex-1"
              placeholder="Endereço ou coordenadas"
            />
            <button
              type="button"
              data-testid={REPORT_FORM.gps}
              onClick={useGPS}
              className="px-3 rounded-xl border border-gray-200 hover:border-[var(--fi-blue)] text-[var(--fi-blue)] transition-colors flex items-center gap-1 text-xs uppercase tracking-widest font-bold"
            >
              <MapPin className="w-4 h-4" />
              GPS
            </button>
          </div>
        </Field>
        <Field label="Quando aconteceu?">
          <input
            data-testid={REPORT_FORM.when}
            type="datetime-local"
            value={form.when}
            onChange={(e) => update("when", e.target.value)}
            className="input"
          />
        </Field>
        <Field label="Foto ou vídeo (opcional)">
          <CitizenAttachment
            testId={REPORT_FORM.media}
            label="Anexar foto ou vídeo curto"
            allowVideo
            value={form.media_url ? { url: form.media_url } : null}
            onChange={(att) => setForm((f) => ({ ...f, media_url: att?.url || "", media_kind: att?.kind || null }))}
          />
        </Field>

        <button
          type="submit"
          data-testid={REPORT_FORM.submit}
          disabled={submitting}
          className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[var(--fi-red)] hover:bg-[var(--fi-red-hover)] text-white font-bold uppercase tracking-widest text-sm py-4 transition-colors disabled:opacity-70"
        >
          {submitting ? "Enviando..." : (<><Send className="w-4 h-4" /> Enviar para Redação</>)}
        </button>
      </form>

      <style>{`
        .input {
          width: 100%;
          padding: 12px 14px;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          font-size: 14px;
          outline: none;
          transition: border-color 0.15s ease;
          font-family: inherit;
        }
        .input:focus { border-color: #0D1B4B; }
      `}</style>

      {success && (
        <SuccessModal
          testId={REPORT_FORM.successModal}
          closeTestId={REPORT_FORM.successClose}
          onClose={() => navigate("/")}
        />
      )}
    </section>
  );
};

const Field = ({ label, error, children }) => (
  <div>
    <label className="block text-xs uppercase tracking-widest font-bold text-[var(--fi-blue)] mb-1.5">
      {label}
    </label>
    {children}
    {error && <div className="text-xs text-[var(--fi-red)] mt-1">{error}</div>}
  </div>
);

export default ReportForm;
